import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";

const PROGRAMS = ["Inpatient Treatment", "Aftercare Options", "Family Care", "Not sure yet"];

const GreenShieldForm = () => {
  const [f, setF] = useState({ name: "", phone: "", email: "", certificate: "", plan_group: "", program: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const u = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim()) { toast.error("Please share your name and phone number."); return; }
    setLoading(true);
    try {
      const details = [
        f.certificate ? `GreenShield Certificate #: ${f.certificate}` : null,
        f.plan_group ? `Plan / Group #: ${f.plan_group}` : null,
        f.message || null,
      ].filter(Boolean).join(" | ");
      await api.post("/leads", {
        name: f.name, phone: f.phone, email: f.email.trim() || null,
        insurance_provider: "GreenShield", program_interest: f.program || "GreenShield coverage inquiry",
        source: "greenshield", message: details, consent: true,
      });
      setDone(true);
      toast.success("Thank you — our team will verify your GreenShield coverage and reach out shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please call us.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="greenshield-success" className="rounded-3xl bg-white border border-line shadow-[0_20px_40px_rgb(0,0,0,0.08)] p-10 text-center">
        <div className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-sage/20 text-forest"><CheckCircle2 className="w-8 h-8" /></div>
        <h3 className="mt-6 font-serif text-3xl text-forest">We’re verifying your GreenShield coverage</h3>
        <p className="mt-3 text-ink2 leading-relaxed">Our admissions team will confirm your benefits with GreenShield and call you back — usually within the hour.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="greenshield-form" className="rounded-3xl bg-white border border-line shadow-[0_20px_40px_rgb(0,0,0,0.08)] p-6 md:p-9">
      <h3 className="font-serif text-2xl text-forest mb-5">GreenShield Coverage — Intake Form</h3>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2"><Label>Your name *</Label><Input data-testid="gs-name" value={f.name} onChange={(e) => u("name", e.target.value)} className="h-12 rounded-xl border-line bg-sand/40" /></div>
        <div className="space-y-2"><Label>Phone *</Label><Input data-testid="gs-phone" value={f.phone} onChange={(e) => u("phone", e.target.value)} className="h-12 rounded-xl border-line bg-sand/40" placeholder="(647) 000-0000" /></div>
        <div className="space-y-2"><Label>Email</Label><Input data-testid="gs-email" type="email" value={f.email} onChange={(e) => u("email", e.target.value)} className="h-12 rounded-xl border-line bg-sand/40" /></div>
        <div className="space-y-2"><Label>Program of interest</Label>
          <Select value={f.program} onValueChange={(v) => u("program", v)}>
            <SelectTrigger data-testid="gs-program" className="h-12 rounded-xl border-line bg-sand/40"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{PROGRAMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label>GreenShield certificate #</Label><Input data-testid="gs-cert" value={f.certificate} onChange={(e) => u("certificate", e.target.value)} className="h-12 rounded-xl border-line bg-sand/40" placeholder="If you have it handy" /></div>
        <div className="space-y-2"><Label>Plan / group #</Label><Input data-testid="gs-group" value={f.plan_group} onChange={(e) => u("plan_group", e.target.value)} className="h-12 rounded-xl border-line bg-sand/40" placeholder="Optional" /></div>
      </div>
      <div className="mt-5 space-y-2"><Label>How can we help? (optional)</Label><Textarea data-testid="gs-message" value={f.message} onChange={(e) => u("message", e.target.value)} rows={3} className="rounded-xl border-line bg-sand/40 resize-none" /></div>
      <Button type="submit" data-testid="gs-submit" disabled={loading} className="mt-6 w-full h-14 rounded-full bg-terracotta hover:bg-[#b45a3d] text-white text-base font-semibold">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify My GreenShield Coverage"}
      </Button>
      <p className="mt-4 flex items-center justify-center gap-2 text-xs text-ink2"><ShieldCheck className="w-4 h-4 text-sage" /> 100% confidential · No obligation</p>
    </form>
  );
};

export default GreenShieldForm;
